import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, TrendingUp, DollarSign, Users, Share2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const AngelDashboard = () => {
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, earningsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/affiliate/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/affiliate/earnings`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setEarnings(earningsRes.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'angel') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const copyReferralCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/products?ref=${stats?.referral_code}`;
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(getReferralLink());
    alert('Referral link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="angel-dashboard-title">Nanaki's Kaur Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user?.name}!</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Total Earnings</span>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-green-600" data-testid="total-earnings">
              {formatINR(stats?.total_earnings || 0)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Pending</span>
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-3xl font-bold text-orange-600">
              {formatINR(stats?.pending_earnings || 0)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Earned (Ready)</span>
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {formatINR(stats?.earned_commissions || 0)}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Total Orders</span>
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-blue-600" data-testid="total-referrals">
              {stats?.total_referrals || 0}
            </div>
          </div>
        </div>

        {/* Referral Code Section */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg shadow-lg p-8 mb-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Your Discount Code</h2>
          <p className="mb-6 opacity-90">
            Share this code with customers for {stats?.commission_rate} discount on their purchase. You earn {stats?.commission_rate} commission!
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <code className="text-2xl font-bold" data-testid="referral-code">{stats?.referral_code}</code>
              <button
                onClick={copyReferralCode}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition flex items-center space-x-2"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Withdrawal Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-bold text-blue-900 mb-2">💰 Withdraw Your Earnings</h3>
          <p className="text-blue-800 mb-3">
            Ready to withdraw your earned commissions? Contact us:
          </p>
          <div className="bg-white rounded-lg p-4 border border-blue-300">
            <p className="text-lg font-bold text-blue-900">📞 {stats?.withdrawal_contact}</p>
            <p className="text-sm text-blue-700 mt-1">Call or WhatsApp to request withdrawal</p>
          </div>
        </div>

        {/* Earnings History */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-2xl font-bold">Earnings — invoice by invoice</h2>
            <p className="text-sm text-gray-600 mt-1">
              Every time a shopper places an order with your code, that invoice is added here.
              <strong> Pending</strong> = awaiting Nanaki to collect payment from the shopper. <strong>Earned</strong> = payment received, queued for your payout.
            </p>
          </div>
          <div className="overflow-x-auto">
            {earnings.length === 0 ? (
              <div className="p-12 text-center text-gray-600">
                <p>No earnings yet. Start sharing your discount code to earn commissions!</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount Given
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {earnings.map((earning) => (
                    <tr key={earning.order_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(earning.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                        #{earning.order_id.slice(-8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatINR(earning.subtotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                        -{formatINR(earning.discount_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                        {formatINR(earning.commission_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          earning.commission_status === 'earned'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {earning.commission_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AngelDashboard;